import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowRight, Clock } from "lucide-react";
import { PageHeader } from "@/components/layout/Navigation";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import api from "@/lib/api";

const faqs = [
  {
    question: "What should I expect during my first visit?",
    answer: "Your first visit typically includes a comprehensive health assessment, review of your medical history, discussion of your health concerns, and if needed, a physical examination. Plan for about 45-60 minutes for new patient appointments."
  },
  {
    question: "Do you accept insurance?",
    answer: "Yes, we accept most major insurance plans. Please contact our office to verify that we accept your specific insurance. We also offer competitive self-pay rates for uninsured patients."
  },
  {
    question: "How do I schedule an appointment?",
    answer: "You can schedule an appointment online through our booking page, call our office during business hours, or send us a message through the contact form. We offer same-day and next-day appointments when available."
  },
  {
    question: "What are your office hours?",
    answer: "Our office is open Monday through Friday from 8:00 AM to 6:00 PM, and Saturday from 9:00 AM to 2:00 PM. We are closed on Sundays and major holidays."
  },
  {
    question: "Do you offer telehealth appointments?",
    answer: "Yes, we offer telehealth appointments for appropriate visits such as follow-ups, medication management, and certain types of consultations. Ask our staff about telehealth options when scheduling."
  },
  {
    question: "How do I request prescription refills?",
    answer: "For existing patients, prescription refill requests can be made through our patient portal, by calling our office, or by having your pharmacy send us a refill request. Please allow 24-48 hours for processing."
  }
];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    service_interest: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.createConsultation(formData);
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        service_interest: "",
        message: ""
      });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader 
        title="Contact Us"
        subtitle="Have questions? We're here to help. Reach out anytime."
        breadcrumbs={[{ name: "Contact" }]}
      />

      <section className="py-20 md:py-32 bg-[#F9F9F7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="gold-divider" />
                <span className="text-sm font-medium tracking-[0.2em] uppercase text-[#888]">Get In Touch</span>
              </div>
              <h2 className="font-display text-4xl text-[#1A1A1A] mb-6">
                We'd Love to
                <br />
                <span className="italic text-[#1B4D3E]">Hear From You</span>
              </h2>
              <p className="text-lg text-[#4A4A4A] font-light leading-relaxed mb-12">
                Whether you have questions about our treatments, want to schedule a consultation, 
                or simply want to learn more about NeoBody, our team is here to assist you.
              </p>

              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-[#1B4D3E] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs tracking-[0.15em] uppercase text-[#888] mb-1">Phone</p>
                    <a href="tel:+13473428822" className="text-xl text-[#1A1A1A] font-medium hover:text-[#1B4D3E] transition-colors">(347) 342-8822</a>
                    <p className="text-sm text-[#4A4A4A]">Mon-Fri 8am-6pm, Sat 9am-3pm</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-[#1B4D3E] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs tracking-[0.15em] uppercase text-[#888] mb-1">Email</p>
                    <p className="text-xl text-[#1A1A1A] font-medium">concierge@neobody.com</p>
                    <p className="text-sm text-[#4A4A4A]">We respond within 24 hours</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-[#1B4D3E] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs tracking-[0.15em] uppercase text-[#888] mb-1">Headquarters</p>
                    <p className="text-xl text-[#1A1A1A] font-medium">Beverly Hills, CA</p>
                    <p className="text-sm text-[#4A4A4A]">9400 Brighton Way, Suite 400</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 md:p-10 shadow-lg" data-testid="contact-form">
                <h3 className="font-display text-2xl text-[#1A1A1A] mb-8">Send Us a Message</h3>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">First Name</label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                      className="form-input"
                      required
                      data-testid="input-first-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Last Name</label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                      className="form-input"
                      required
                      data-testid="input-last-name"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="form-input"
                    required
                    data-testid="input-email"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="form-input"
                    required
                    data-testid="input-phone"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Service Interest</label>
                  <select
                    value={formData.service_interest}
                    onChange={(e) => setFormData(prev => ({ ...prev, service_interest: e.target.value }))}
                    className="form-input"
                    required
                    data-testid="select-service"
                  >
                    <option value="">Select a service...</option>
                    <option value="primary-care">Primary Care</option>
                    <option value="weight-management">Weight Management</option>
                    <option value="preventive-medicine">Preventive Medicine</option>
                    <option value="mens-health">Men's Health</option>
                    <option value="womens-health">Women's Health</option>
                    <option value="wellness-exams">Wellness Exams</option>
                    <option value="general">General Inquiry</option>
                  </select>
                </div>
                <div className="mb-8">
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Message</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    className="form-input resize-none"
                    rows={4}
                    required
                    data-testid="textarea-message"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-gold w-full justify-center disabled:opacity-50"
                  data-testid="submit-contact"
                >
                  {submitting ? "Sending..." : "Send Message"}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 md:py-32 bg-white">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="gold-divider" />
              <span className="text-sm font-medium tracking-[0.2em] uppercase text-[#888]">FAQ</span>
              <div className="gold-divider" />
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-[#1A1A1A]">Frequently Asked Questions</h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="faq-item"
                data-testid={`faq-${index}`}
              >
                <AccordionTrigger className="faq-trigger hover:no-underline">
                  <span className="text-left pr-4">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="faq-content">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
